import { mimeTypeFileExtensionMap } from '../file-extension-map';
import { AwsUploadService } from './aws-upload.service';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, Input, OnInit, output, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RootReducer, Store } from '@app/app.reducers';
import { generateFileBucketUrl } from '@app/product/utils/product-utils';
import { IconComponent } from '@app/shared/components/icon/icon.component';
import { FileUploadStatus, getUploads } from '@app/shared/reducers/upload.reducer';
import { castArray, getDebug } from '@app/shared/utils/util';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCloudArrowDown } from '@fortawesome/pro-solid-svg-icons/faCloudArrowDown';
import { faFile } from '@fortawesome/pro-solid-svg-icons/faFile';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons/faTrashCan';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import formatBytes from 'bytes';
import mimeMatch from 'mime-match';
import { BehaviorSubject } from 'rxjs';

export interface FileItem {
	key: string;
	filename: string;
	fileUrl?: string;
	fileSize?: string;
	status?: FileUploadStatus;
	file?: File;
}

/** Component for uploading files. */
@UntilDestroy()
@Component({
	standalone: true,
	selector: 'g-upload',
	templateUrl: './upload.component.html',
	styleUrls: ['./upload.component.scss'],
	imports: [IconComponent, NgClass, AsyncPipe, NgIf, NgFor, FlexLayoutModule],
})
export class UploadComponent implements OnInit {
	@Input() multiple = false;
	@Input() readonly fileType?: string; // unused
	@Input() readonly options?: any; // unused
	readonly onUploadComplete = output<FileItem>();
	readonly onUploadStart = output<FileItem>();
	readonly onClear = output<FileItem>();
	readonly onFiletypeError = output<File[]>();
	@ViewChild('fileInput', { static: true }) readonly fileInput!: ElementRef<HTMLInputElement>;
	private readonly debug = getDebug('UploadComponent');
	files: FileItem[] = [];
	isOver = true;
	readonly isUploading$ = new BehaviorSubject(false);
	readonly isUploadError$ = new BehaviorSubject(false);
	readonly icons = {
		faFile,
		faSpinner,
		faCloudArrowDown,
		faTrashCan,
		faTriangleExclamation,
	};
	readonly statusIndicator: Record<
		FileUploadStatus,
		{ description: string; icon: IconDefinition; class?: string; pulse?: boolean }
	> = {
		[FileUploadStatus.Uploading]: {
			description: $localize`:@@UploadStatusUploading:Uploading...`,
			icon: faSpinner,
			pulse: true,
		},
		[FileUploadStatus.Success]: {
			description: $localize`:@@UploadStatusUploading:Upload complete!`,
			icon: faCloudArrowDown,
		},
		[FileUploadStatus.Error]: {
			description: $localize`:@@UploadStatusInfected:Upload failed!`,
			icon: faTriangleExclamation,
			class: 'upload-failed',
		},
	};
	readonly statusType = FileUploadStatus;
	private acceptedFiletypesList: string[] = [];
	private acceptedFileExtensions!: Set<string>;

	constructor(
		private readonly awsUploadService: AwsUploadService,
		private readonly store: Store<RootReducer.State>,
	) {}

	@Input()
	set uploadedFiles(files: FileItem | FileItem[] | undefined) {
		// Do not override if files have been changed by the user.
		if (this.files?.length > 0) return;
		files = castArray(files || []);
		if (!files?.length) return;
		this.debug('uploadedFiles', files);
		this.files = files.map((file) => {
			if (!file.fileUrl) {
				return { ...file, fileUrl: generateFileBucketUrl(file.key) };
			}
			// istanbul ignore next
			return file;
		});
	}
	@Input() set acceptedFiletypes(fileTypes: string) {
		// fileTypes is a string like 'image/jpeg,image/png'
		this.acceptedFiletypesList = (fileTypes || '')
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
	}

	ngOnInit(): void {
		this.buildFileExtensionMap();

		this.store
			.select(getUploads)
			.pipe(untilDestroyed(this))
			.subscribe((uploads) => this.updateFileStatus(uploads));
	}

	chooseFile(event: Event): void {
		event.preventDefault();
		this.fileInput.nativeElement.click();
	}

	handleFileInput(event: Event): void {
		const files = Array.from((event.target as HTMLInputElement).files || []);
		this.doUpload(files);
	}

	handleDrop(event: DragEvent): void {
		this.handleDrag(event);
		let files: File[] = [];

		if (event.dataTransfer?.items?.length ?? 0 > 0) {
			files = [...(event.dataTransfer?.items ?? [])]
				.filter((item) => item.kind === 'file')
				.map((item) => item.getAsFile()!);
		} else if (event.dataTransfer?.files?.length ?? 0 > 0) {
			files = [...(event.dataTransfer?.files ?? [])];
		}

		this.doUpload(this.multiple ? files : [files[0]]);
	}

	handleDrag(event: DragEvent): void {
		event.preventDefault();
		this.isOver = event.type === 'dragover';
	}

	doUpload(files: File[]): void {
		if (!this.multiple) {
			this.files = []; // Clear files when only one file can be uploaded.
		}
		if (!Array.isArray(files) || files.length === 0) return;

		const invalidFiles = files.filter((file) => this.isValidFile(file) === false);
		if (invalidFiles.length > 0) {
			this.onFiletypeError.emit(invalidFiles);
			return;
		}

		for (const file of files) {
			// TODO: Handle errors better.
			// istanbul ignore if -- TODO: Refactor and add tests after proto.
			const { key, filename } = this.sanitizeFilename(file);
			const item: FileItem = {
				key,
				file,
				filename,
				fileSize: formatBytes(file.size, { unitSeparator: ' ' }),
				fileUrl: generateFileBucketUrl(key),
				status: FileUploadStatus.Uploading,
			};
			this.files.push(item);
			this.onUploadStart.emit(item);
			void this.awsUploadService.upload(key, file);
		}
	}

	clearInputFile(fileItem: FileItem): void {
		const idx = this.files.findIndex((item) => item === fileItem);
		if (idx === -1) throw new Error('Trying to remove file that does not exist');
		this.files.splice(idx, 1);
		this.onClear.emit(fileItem);
	}

	private isValidFile(file: File): boolean {
		if (!(file instanceof File)) return false;
		if (!this.acceptedFiletypesList?.length) return true;
		// Windows doesn't have file type for files that don't have an associated program.
		// Validate by file extension instead.
		if (!file.type) {
			const fileExtension = file.name.toLowerCase().split('.').pop()!;
			this.debug('isValidFile', fileExtension, this.acceptedFileExtensions);
			return this.acceptedFileExtensions.has(fileExtension);
		}
		this.debug('isValidFile', this.acceptedFiletypesList, file.type);
		return this.acceptedFiletypesList.some(mimeMatch(file.type));
	}

	private sanitizeFilename(file: File): { key: string; filename: string } {
		const filename = file.name.replaceAll(/[^\wÀ-ÿ .\-+!@£$€%&()=^#]/gi, '');
		const filenameForKey = filename.replaceAll(/\s/gi, '_');
		const key = `${crypto.randomUUID()}-${filenameForKey.replaceAll(/[^\w.-]/gi, '')}`;
		return { key, filename };
	}

	private buildFileExtensionMap() {
		this.acceptedFileExtensions = new Set<string>();
		for (const type of this.acceptedFiletypesList) {
			const extensions = mimeTypeFileExtensionMap.get(type);
			if (extensions?.length ?? 0 > 0) {
				for (const ext of extensions!) this.acceptedFileExtensions.add(ext);
			} else {
				this.acceptedFileExtensions.add(type.split('/').pop()!);
			}
		}
	}

	/** Check statuses for all the files we are uploading. */
	private updateFileStatus(uploads: Record<string, FileUploadStatus>): void {
		this.debug('updateFileStatus', this.files, uploads);
		let isUploading = false;
		let isError = false;
		for (const item of this.files) {
			if (item.status === FileUploadStatus.Success) continue; // Skip already completed files
			const status = uploads[item.key];
			item.status = status;
			switch (status) {
				case FileUploadStatus.Uploading:
					isUploading = true;
					break;
				case FileUploadStatus.Error:
					isError = true;
					break;
				case FileUploadStatus.Success:
					this.onUploadComplete.emit(item);
					break;
			}
		}
		this.isUploading$.next(isUploading);
		this.isUploadError$.next(isError);
	}
}
